import { useState, useEffect } from 'react';
import { useAuth } from '@/context/AuthContext';
import AdminLayout from '@/components/AdminLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { toast } from 'sonner';
import axios from 'axios';
import { Plus, Scissors, Pencil, Trash2, Clock, DollarSign, Store, RotateCcw, ChevronDown, Check, X } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const iconOptions = [
  { value: 'scissors', label: 'Tijeras' },
  { value: 'droplet', label: 'Gota' },
  { value: 'hand', label: 'Mano' },
];

export default function AdminServices() {
  const { user, getAuthHeaders } = useAuth();
  const [services, setServices] = useState([]);
  const [branches, setBranches] = useState([]);
  const [selectedBranch, setSelectedBranch] = useState('base');
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingService, setEditingService] = useState(null);
  const [branchSelectorOpen, setBranchSelectorOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    price: '',
    duration_minutes: '30',
    icon: 'scissors',
    branch_ids: [],  // empty array = all branches
    is_extra: false,
  });

  const isMasterAdmin = user?.role === 'master_admin';

  useEffect(() => {
    fetchBranches();
    fetchServices();
  }, []);

  useEffect(() => {
    fetchServices();
  }, [selectedBranch]);

  const fetchBranches = async () => {
    try {
      const response = await axios.get(`${API}/branches`, getAuthHeaders());
      setBranches(response.data);
    } catch (error) {
      // error handled by toast
    }
  };

  const fetchServices = async () => {
    setLoading(true);
    try {
      if (selectedBranch === 'base') {
        const response = await axios.get(`${API}/services`, getAuthHeaders());
        setServices(response.data.map(s => ({ ...s, effective_price: s.price, has_branch_price: false })));
      } else {
        const response = await axios.get(
          `${API}/branch-prices/by-branch/${selectedBranch}?item_type=service`,
          getAuthHeaders()
        );
        setServices(response.data.items);
      }
    } catch (error) {
      toast.error('Error al cargar servicios');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const payload = {
      name: formData.name,
      price: parseFloat(formData.price),
      duration_minutes: parseInt(formData.duration_minutes),
      icon: formData.icon,
      branch_ids: formData.branch_ids.length > 0 ? formData.branch_ids : null,  // null = all branches
      is_extra: !!formData.is_extra,
    };
    
    try {
      if (editingService) {
        if (selectedBranch === 'base') {
          // Update base price
          await axios.put(`${API}/services/${editingService.id}`, payload, getAuthHeaders());
          toast.success('Servicio actualizado');
        } else {
          // Update branch-specific price
          await axios.post(`${API}/branch-prices`, {
            item_id: editingService.id,
            item_type: 'service',
            branch_id: selectedBranch,
            price: parseFloat(formData.price)
          }, getAuthHeaders());
          toast.success('Precio de sucursal actualizado');
        }
      } else {
        await axios.post(`${API}/services`, payload, getAuthHeaders());
        toast.success('Servicio creado');
      }
      setIsDialogOpen(false);
      resetForm();
      fetchServices();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar');
    }
  };

  const resetForm = () => {
    setFormData({ name: '', price: '', duration_minutes: '30', icon: 'scissors', branch_ids: [], is_extra: false });
    setEditingService(null);
    setBranchSelectorOpen(false);
  };

  const handleEdit = (service) => {
    setEditingService(service);
    setFormData({
      name: service.name,
      price: (service.branch_price || service.effective_price || service.price).toString(),
      duration_minutes: service.duration_minutes.toString(),
      icon: service.icon,
      branch_ids: service.branch_ids || [],
      is_extra: !!service.is_extra,
    });
    setIsDialogOpen(true);
  };

  const toggleBranch = (branchId) => {
    setFormData(prev => {
      const current = prev.branch_ids || [];
      if (current.includes(branchId)) {
        return { ...prev, branch_ids: current.filter(id => id !== branchId) };
      } else {
        return { ...prev, branch_ids: [...current, branchId] };
      }
    });
  };

  const selectAllBranches = () => {
    setFormData(prev => ({ ...prev, branch_ids: [] }));
  };

  const getSelectedBranchesText = () => {
    if (!formData.branch_ids || formData.branch_ids.length === 0) {
      return "🌐 Todas las sucursales";
    }
    if (formData.branch_ids.length === 1) {
      const branch = branches.find(b => b.id === formData.branch_ids[0]);
      return branch?.name || "1 sucursal";
    }
    return `${formData.branch_ids.length} sucursales seleccionadas`;
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Eliminar este servicio?')) return;
    try {
      await axios.delete(`${API}/services/${id}`, getAuthHeaders());
      toast.success('Servicio eliminado');
      fetchServices();
    } catch (error) {
      toast.error('Error al eliminar');
    }
  };

  const handleResetBranchPrice = async (service) => {
    if (!window.confirm(`¿Restaurar precio base para "${service.name}" en esta sucursal?`)) return;
    try {
      // Find and delete the branch price
      const pricesRes = await axios.get(
        `${API}/branch-prices?branch_id=${selectedBranch}&item_type=service`,
        getAuthHeaders()
      );
      const branchPrice = pricesRes.data.find(p => p.item_id === service.id);
      if (branchPrice) {
        await axios.delete(`${API}/branch-prices/${branchPrice.id}`, getAuthHeaders());
        toast.success('Precio restaurado al base');
        fetchServices();
      }
    } catch (error) {
      toast.error('Error al restaurar precio');
    }
  };

  const getBranchName = () => {
    if (selectedBranch === 'base') return null;
    return branches.find(b => b.id === selectedBranch)?.name || '';
  };

  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="admin-services">
        {/* Header */}
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <h1 className="font-heading text-3xl text-white tracking-tight">Servicios</h1>
            <p className="text-zinc-500 mt-1">
              {selectedBranch === 'base' 
                ? 'Precios base para todas las sucursales'
                : `Precios para ${getBranchName()}`}
            </p>
          </div>
          
          <div className="flex items-center gap-3">
            {/* Branch Selector - Only for Master Admin */}
            {isMasterAdmin && (
              <div className="flex items-center gap-2">
                <Store className="w-4 h-4 text-zinc-500" />
                <Select value={selectedBranch} onValueChange={setSelectedBranch}>
                  <SelectTrigger className="w-[220px] bg-zinc-800 border-zinc-700 text-white">
                    <SelectValue placeholder="Seleccionar sucursal" />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-800 border-zinc-700">
                    <SelectItem value="base" className="text-amber-500">
                      💰 Precios Base (Global)
                    </SelectItem>
                    {branches.map((branch) => (
                      <SelectItem key={branch.id} value={branch.id} className="text-white">
                        {branch.business_name?.split(' ')[0]} - {branch.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {selectedBranch === 'base' && (
              <Dialog open={isDialogOpen} onOpenChange={(open) => { setIsDialogOpen(open); if (!open) resetForm(); }}>
                <DialogTrigger asChild>
                  <Button 
                    className="bg-gold text-black hover:bg-gold-dark"
                    data-testid="add-service-btn"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Nuevo Servicio
                  </Button>
                </DialogTrigger>
                <DialogContent className="bg-zinc-900 border-zinc-800">
                  <DialogHeader>
                    <DialogTitle className="font-heading text-xl text-white">
                      Nuevo Servicio
                    </DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                    <div className="space-y-2">
                      <Label className="text-zinc-400">Nombre</Label>
                      <Input
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        placeholder="Corte Clásico"
                        className="bg-zinc-800 border-zinc-700 text-white"
                        data-testid="service-name-input"
                        required
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label className="text-zinc-400">Precio ($)</Label>
                        <Input
                          type="number"
                          step="0.01"
                          value={formData.price}
                          onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                          placeholder="150.00"
                          className="bg-zinc-800 border-zinc-700 text-white"
                          data-testid="service-price-input"
                          required
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-zinc-400">Duración (min)</Label>
                        <Input
                          type="number"
                          value={formData.duration_minutes}
                          onChange={(e) => setFormData({ ...formData, duration_minutes: e.target.value })}
                          placeholder="30"
                          className="bg-zinc-800 border-zinc-700 text-white"
                          data-testid="service-duration-input"
                          required
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label className="text-zinc-400">Icono</Label>
                      <Select
                        value={formData.icon}
                        onValueChange={(value) => setFormData({ ...formData, icon: value })}
                      >
                        <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-zinc-800 border-zinc-700">
                          {iconOptions.map((opt) => (
                            <SelectItem key={opt.value} value={opt.value} className="text-white">
                              {opt.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    {/* is_extra flag */}
                    <label className="flex items-center gap-3 p-3 rounded-sm bg-zinc-800/50 border border-zinc-700 cursor-pointer hover:border-orange-500/50 transition-colors" data-testid="is-extra-label">
                      <input
                        type="checkbox"
                        checked={!!formData.is_extra}
                        onChange={(e) => setFormData({ ...formData, is_extra: e.target.checked })}
                        className="w-4 h-4 rounded bg-zinc-900 border-zinc-600 text-orange-500 focus:ring-orange-500"
                        data-testid="service-is-extra-checkbox"
                      />
                      <div className="flex-1">
                        <span className="text-sm text-white font-medium">Marcar como EXTRA</span>
                        <p className="text-[10px] text-zinc-500">Servicios EXTRA se filtran aparte en metas y reportes</p>
                      </div>
                      {formData.is_extra && (
                        <span className="text-[10px] px-2 py-0.5 rounded bg-orange-500/20 text-orange-400 font-semibold uppercase tracking-wider">Extra</span>
                      )}
                    </label>
                    
                    {/* Multi-select branches */}
                    <div className="space-y-2">
                      <Label className="text-zinc-400">Sucursales</Label>
                      <Popover open={branchSelectorOpen} onOpenChange={setBranchSelectorOpen}>
                        <PopoverTrigger asChild>
                          <Button
                            variant="outline"
                            className="w-full justify-between bg-zinc-800 border-zinc-700 text-white hover:bg-zinc-700"
                            data-testid="branch-selector-trigger"
                          >
                            <span className={formData.branch_ids?.length === 0 ? 'text-amber-500' : ''}>
                              {getSelectedBranchesText()}
                            </span>
                            <ChevronDown className="w-4 h-4 ml-2 opacity-50" />
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-[320px] p-0 bg-zinc-800 border-zinc-700" align="start">
                          <div className="p-2 max-h-[300px] overflow-y-auto">
                            {/* Select All Option */}
                            <button
                              type="button"
                              onClick={selectAllBranches}
                              className={`w-full flex items-center gap-3 p-2 rounded-sm transition-colors ${
                                formData.branch_ids?.length === 0 
                                  ? 'bg-amber-500/20 text-amber-500' 
                                  : 'hover:bg-zinc-700 text-zinc-300'
                              }`}
                            >
                              <div className={`w-4 h-4 rounded-sm border flex items-center justify-center ${
                                formData.branch_ids?.length === 0 
                                  ? 'bg-amber-500 border-amber-500' 
                                  : 'border-zinc-600'
                              }`}>
                                {formData.branch_ids?.length === 0 && <Check className="w-3 h-3 text-black" />}
                              </div>
                              <span>🌐 Todas las sucursales</span>
                            </button>
                            
                            {branches.length > 0 && (
                              <div className="border-t border-zinc-700 my-2"></div>
                            )}
                            
                            {/* Branch list grouped by business */}
                            {branches.length === 0 ? (
                              <p className="text-xs text-zinc-500 text-center py-4">
                                No hay sucursales registradas.<br/>
                                Crea sucursales primero en Admin → Sucursales
                              </p>
                            ) : (
                              [...new Set(branches.map(b => b.business_name))].map(business => {
                                const businessBranches = branches.filter(b => b.business_name === business);
                                if (businessBranches.length === 0) return null;
                                
                                return (
                                  <div key={business} className="mb-2">
                                    <p className="text-xs text-zinc-500 px-2 py-1 uppercase tracking-widest">
                                      {business || 'Sin negocio'}
                                    </p>
                                    {businessBranches.map(branch => {
                                      const isSelected = formData.branch_ids?.includes(branch.id);
                                      return (
                                        <button
                                          key={branch.id}
                                          type="button"
                                          onClick={() => toggleBranch(branch.id)}
                                          className={`w-full flex items-center gap-3 p-2 rounded-sm transition-colors ${
                                            isSelected 
                                              ? 'bg-gold/20 text-gold' 
                                              : 'hover:bg-zinc-700 text-zinc-300'
                                          }`}
                                        >
                                          <div className={`w-4 h-4 rounded-sm border flex items-center justify-center ${
                                            isSelected 
                                              ? 'bg-gold border-gold' 
                                              : 'border-zinc-600'
                                          }`}>
                                            {isSelected && <Check className="w-3 h-3 text-black" />}
                                          </div>
                                          <span className="text-sm">{branch.name}</span>
                                        </button>
                                      );
                                    })}
                                  </div>
                                );
                              })
                            )}
                          </div>
                        </PopoverContent>
                      </Popover>
                      <p className="text-xs text-zinc-500">
                        {formData.branch_ids?.length === 0 
                          ? 'El servicio estará disponible en todas las sucursales'
                          : 'El servicio solo estará disponible en las sucursales seleccionadas'}
                      </p>
                    </div>
                    
                    <div className="flex gap-3 pt-4">
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => setIsDialogOpen(false)}
                        className="flex-1 border-zinc-700 text-zinc-300 hover:bg-zinc-800"
                      >
                        Cancelar
                      </Button>
                      <Button
                        type="submit"
                        className="flex-1 bg-gold text-black hover:bg-gold-dark"
                        data-testid="service-submit-btn"
                      >
                        {editingService ? 'Actualizar' : 'Crear'}
                      </Button>
                    </div>
                  </form>
                </DialogContent>
              </Dialog>
            )}
          </div>
        </div>

        {/* Info Banner for Branch Pricing */}
        {selectedBranch !== 'base' && isMasterAdmin && (
          <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4">
            <p className="text-blue-400 text-sm">
              💡 Editando precios para <strong>{getBranchName()}</strong>. 
              Los precios que modifiques aquí solo aplicarán a esta sucursal. 
              Los servicios sin precio personalizado usarán el precio base.
            </p>
          </div>
        )}

        {/* Services Grid */}
        {loading ? (
          <div className="text-center py-12 text-zinc-500">Cargando...</div>
        ) : services.length === 0 ? (
          <Card className="bg-zinc-900/50 border-zinc-800 p-12 text-center">
            <Scissors className="w-12 h-12 text-zinc-700 mx-auto mb-4" />
            <p className="text-zinc-500">No hay servicios registrados</p>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {services.map((service) => (
              <Card 
                key={service.id} 
                className={`bg-zinc-900/50 border-zinc-800 p-5 hover:border-zinc-700 transition-colors ${
                  service.has_branch_price ? 'ring-1 ring-blue-500/50' : ''
                }`}
                data-testid={`service-card-${service.id}`}
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="w-10 h-10 rounded-sm bg-gold/10 flex items-center justify-center">
                    <Scissors className="w-5 h-5 text-gold" />
                  </div>
                  <div className="flex gap-1">
                    {isMasterAdmin && (
                      <button
                        onClick={() => handleEdit(service)}
                        className="w-7 h-7 rounded-sm bg-zinc-800 hover:bg-zinc-700 flex items-center justify-center text-zinc-400 hover:text-white transition-colors"
                        title="Editar precio"
                      >
                        <Pencil className="w-3 h-3" />
                      </button>
                    )}
                    {selectedBranch !== 'base' && service.has_branch_price && (
                      <button
                        onClick={() => handleResetBranchPrice(service)}
                        className="w-7 h-7 rounded-sm bg-zinc-800 hover:bg-amber-500/20 flex items-center justify-center text-zinc-400 hover:text-amber-500 transition-colors"
                        title="Restaurar precio base"
                      >
                        <RotateCcw className="w-3 h-3" />
                      </button>
                    )}
                    {selectedBranch === 'base' && (
                      <button
                        onClick={() => handleDelete(service.id)}
                        className="w-7 h-7 rounded-sm bg-zinc-800 hover:bg-red-500/20 flex items-center justify-center text-zinc-400 hover:text-red-500 transition-colors"
                      >
                        <Trash2 className="w-3 h-3" />
                      </button>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2 mb-1">
                  <h3 className="font-medium text-white">{service.name}</h3>
                  {service.is_extra && (
                    <span className="text-[9px] px-1.5 py-0.5 rounded bg-orange-500/20 text-orange-400 font-semibold uppercase tracking-wider" data-testid={`service-extra-badge-${service.id}`}>
                      Extra
                    </span>
                  )}
                </div>
                {service.branch_names && service.branch_names.length > 0 && (
                  <p className="text-xs text-emerald-500 mb-2">
                    <Store className="w-3 h-3 inline mr-1" />
                    {service.branch_names.length === 1 
                      ? `Solo: ${service.branch_names[0]}`
                      : `${service.branch_names.length} sucursales`}
                  </p>
                )}
                {(!service.branch_ids || service.branch_ids.length === 0) && selectedBranch === 'base' && (
                  <p className="text-xs text-zinc-500 mb-2">🌐 Todas las sucursales</p>
                )}
                <div className="flex items-center justify-between text-sm">
                  <div className="flex items-center gap-1 text-zinc-500">
                    <Clock className="w-3 h-3" />
                    <span>{service.duration_minutes} min</span>
                  </div>
                  <div className="text-right">
                    {service.has_branch_price ? (
                      <div>
                        <div className="flex items-center gap-1 text-blue-400 font-semibold">
                          <DollarSign className="w-3 h-3" />
                          <span>{service.effective_price.toFixed(2)}</span>
                        </div>
                        <p className="text-xs text-zinc-600 line-through">
                          Base: ${service.price.toFixed(2)}
                        </p>
                      </div>
                    ) : (
                      <div className="flex items-center gap-1 text-gold font-semibold">
                        <DollarSign className="w-3 h-3" />
                        <span>{(service.effective_price || service.price).toFixed(2)}</span>
                      </div>
                    )}
                  </div>
                </div>
                {service.has_branch_price && (
                  <div className="mt-2 pt-2 border-t border-zinc-800">
                    <span className="text-xs text-blue-400">✓ Precio personalizado</span>
                  </div>
                )}
              </Card>
            ))}
          </div>
        )}

        {/* Edit Price Dialog for Branch */}
        {selectedBranch !== 'base' && editingService && (
          <Dialog open={isDialogOpen} onOpenChange={(open) => { setIsDialogOpen(open); if (!open) resetForm(); }}>
            <DialogContent className="bg-zinc-900 border-zinc-800">
              <DialogHeader>
                <DialogTitle className="font-heading text-xl text-white">
                  Editar Precio - {getBranchName()}
                </DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                <div className="p-3 bg-zinc-800 rounded-lg">
                  <p className="text-white font-medium">{editingService.name}</p>
                  <p className="text-xs text-zinc-500">Precio base: ${editingService.price.toFixed(2)}</p>
                </div>
                <div className="space-y-2">
                  <Label className="text-zinc-400">Precio para esta sucursal ($)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={formData.price}
                    onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                    placeholder="150.00"
                    className="bg-zinc-800 border-zinc-700 text-white text-lg"
                    data-testid="branch-price-input"
                    required
                    autoFocus
                  />
                </div>
                <div className="flex gap-3 pt-4">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setIsDialogOpen(false)}
                    className="flex-1 border-zinc-700 text-zinc-300 hover:bg-zinc-800"
                  >
                    Cancelar
                  </Button>
                  <Button
                    type="submit"
                    className="flex-1 bg-gold text-black hover:bg-gold-dark"
                  >
                    Guardar Precio
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        )}
      </div>
    </AdminLayout>
  );
}
